import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './StreamingManagement.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const StreamingManagement = ({ hasPermission }) => {
    const [streamers, setStreamers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [newStreamer, setNewStreamer] = useState({
        username: '',
        platform: 'twitch',
        channel: '',
        displayName: '',
        category: 'creator_plus',
        twitchLink: '',
        tiktokLink: '',
        youtubeLink: '',
        instagramLink: '',
        tiktokUsername: ''
    });
    const [isAdding, setIsAdding] = useState(false);
    const [editingStreamer, setEditingStreamer] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [activeStreamEmbed, setActiveStreamEmbed] = useState(null); // Track which streamer's embed is open

    const loadStreamers = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('discord_token');
            const response = await axios.get(`${API_BASE}/api/streamers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Handle both array and object responses
            const data = response.data;
            if (Array.isArray(data)) {
                setStreamers(data);
            } else if (data && typeof data === 'object' && data.data) {
                // Handle { success: true, data: [...] } format
                setStreamers(data.data || []);
            } else {
                setStreamers([]);
            }
        } catch (err) {
            console.error('Error loading streamers:', err);
            alert(`Fehler beim Laden der Streamer: ${err.response?.status || 'Unknown'} - ${err.response?.statusText || 'Serverfehler'}`);
        } finally {
            setLoading(false);
        }
    }, []);

    const refreshStatuses = useCallback(async () => {
        try {
            setRefreshing(true);
            const token = localStorage.getItem('discord_token');
            await Promise.all([
                axios.post(`${API_BASE}/api/twitch/update-status`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.post(`${API_BASE}/api/tiktok/update-status`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(err => console.error('Error updating TikTok status:', err)),
                axios.post(`${API_BASE}/api/youtube/update-status`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(err => console.error('Error updating YouTube status:', err))
            ]);
            await loadStreamers();
        } catch (err) {
            console.error('Error refreshing statuses:', err);
        } finally {
            setRefreshing(false);
        }
    }, [loadStreamers]);

    useEffect(() => {
        loadStreamers();
        
        // Auto-refresh every 30 seconds
        const interval = setInterval(() => {
            refreshStatuses();
        }, 30000);
        
        return () => clearInterval(interval);
    }, [refreshStatuses, loadStreamers]);

    const handleAddStreamer = async (e) => {
        e.preventDefault();
        if (!newStreamer.username || !newStreamer.channel) {
            alert(`Bitte alle erforderlichen Felder ausfüllen:\n- Discord ID: ${newStreamer.username ? '✓' : '✗'}\n- Kanalname: ${newStreamer.channel ? '✓' : '✗'}`);
            return;
        }

        try {
            setIsAdding(true);
            const token = localStorage.getItem('discord_token');
            await axios.post(`${API_BASE}/api/streamers`, {
                username: newStreamer.username,
                platform: newStreamer.platform,
                channel: newStreamer.channel,
                displayName: newStreamer.displayName,
                category: newStreamer.category,
                twitchLink: newStreamer.twitchLink,
                tiktokLink: newStreamer.tiktokLink,
                youtubeLink: newStreamer.youtubeLink,
                instagramLink: newStreamer.instagramLink,
                tiktokUsername: newStreamer.platform === 'tiktok' ? newStreamer.channel : newStreamer.tiktokUsername
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            // Erfolgreich hinzugefügt - benutzerdefinierte Notification anzeigen
            const categoryLabel = newStreamer.category === 'creator' ? 'Creator' : 'Creator+';
            const successMsg = `✅ Streamer erfolgreich hinzugefügt!\n\nDetails:\n- Discord ID: ${newStreamer.username}\n- Plattform: ${newStreamer.platform.toUpperCase()}\n- Kanalname: ${newStreamer.channel}\n- Anzeigename: ${newStreamer.displayName || 'Nicht angegeben'}\n- Kategorie: ${categoryLabel}`;
            alert(successMsg);
            
            setNewStreamer({ 
                username: '', 
                platform: 'twitch', 
                channel: '', 
                displayName: '', 
                category: 'creator_plus',
                twitchLink: '',
                tiktokLink: '',
                youtubeLink: '',
                instagramLink: '',
                tiktokUsername: ''
            });
            await loadStreamers();
        } catch (err) {
            console.error('Error adding streamer:', err);
            console.error('Response data:', err.response?.data);
            console.error('Response status:', err.response?.status);
            const errorMessage = err.response?.data?.message || err.message || 'Unbekannter Fehler';
            const errorDetails = `Fehler beim Hinzufügen des Streamers:\n\nStreamer: ${newStreamer.username}\nPlattform: ${newStreamer.platform.toUpperCase()}\nChannel: ${newStreamer.channel}\n\nFehlermeldung: ${errorMessage}\n\nStatus: ${err.response?.status || 'Unknown'}`;
            alert(errorDetails);
        } finally {
            setIsAdding(false);
        }
    };

    const handleRemoveStreamer = async (streamerId) => {
        // Get streamer info before removal for the confirmation
        const streamerToRemove = streamers.find(s => s.id === streamerId);
        if (!window.confirm(`⚠️ Streamer wirklich entfernen?\n\nStreamer: ${streamerToRemove?.display_name || streamerToRemove?.username || streamerId}\nPlattform: ${streamerToRemove?.platform?.toUpperCase() || 'Unknown'}\nChannel: ${streamerToRemove?.channel || 'Unknown'}\n\nDiese Aktion kann nicht rückgängig gemacht werden!`)) return;
        
        try {
            const token = localStorage.getItem('discord_token');
            await axios.delete(`${API_BASE}/api/streamers/${streamerId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert(`✅ Streamer "${streamerToRemove?.display_name || streamerToRemove?.username}" wurde erfolgreich entfernt.`);
            await loadStreamers();
        } catch (err) {
            console.error('Error removing streamer:', err);
            alert(`Fehler beim Entfernen des Streamers:\n\nStreamer ID: ${streamerId}\nFehler: ${err.response?.data?.message || err.message || 'Unknown'}\n\nStatus: ${err.response?.status || 'Unknown'}`);
        }
    };

    const handleEditStreamer = async (updatedFields) => {
        if (!editingStreamer) return;

        try {
            setIsUpdating(true);
            const token = localStorage.getItem('discord_token');
            
            await axios.put(`${API_BASE}/api/streamers/${editingStreamer.id}`, updatedFields, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert(`✅ Streamer "${editingStreamer.display_name || editingStreamer.username}" wurde aktualisiert.`);
            setEditingStreamer(null);
            await loadStreamers();
        } catch (err) {
            console.error('Error updating streamer:', err);
            alert(`Fehler beim Aktualisieren des Streamers:\n\n${err.response?.data?.message || err.message}`);
        } finally {
            setIsUpdating(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'live': return '#22c55e';
            case 'offline': return '#ef4444';
            case 'unknown': return '#9ca3af';
            default: return '#9ca3af';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'live': return 'LIVE';
            case 'offline': return 'OFFLINE';
            case 'unknown': return 'UNBEKANNT';
            default: return 'UNBEKANNT';
        }
    };

    const getCategoryText = (category) => {
        switch (category) {
            case 'creator_plus': return 'Creator+';
            case 'creator': return 'Creator';
            default: return 'Creator';
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'creator_plus': return '#8b5cf6';
            case 'creator': return '#3b82f6';
            default: return '#8b5cf6';
        }
    };

    // Render a single streamer card
    const renderStreamerCard = (streamer) => (
        <div key={streamer.id} className="streamer-card">
            <div className="streamer-card-header">
                <span className={`platform-badge platform-${streamer.platform}`}>
                    {streamer.platform.toUpperCase()}
                </span>
                <span className="category-badge-small" style={{ backgroundColor: getCategoryColor(streamer.category) }}>
                    {getCategoryText(streamer.category)}
                </span>
                <span
                    className={`status-badge status-${streamer.status}`}
                    onClick={() => {
                        if (streamer.status === 'live') {
                            if (streamer.platform === 'tiktok') window.open(`https://tiktok.com/@${streamer.channel}/live`, '_blank');
                            else if (streamer.platform === 'youtube') window.open(`https://youtube.com/${streamer.channel.startsWith('@') ? streamer.channel : '@'+streamer.channel}/live`, '_blank');
                            else toggleStreamEmbed(streamer.id);
                        } else {
                            if (streamer.platform === 'tiktok') window.open(`https://tiktok.com/@${streamer.channel}`, '_blank');
                            else if (streamer.platform === 'youtube') window.open(`https://youtube.com/${streamer.channel.startsWith('@') ? streamer.channel : '@'+streamer.channel}`, '_blank');
                            else window.open(`https://twitch.tv/${streamer.channel}`, '_blank');
                        }
                    }}
                    style={{ cursor: streamer.status === 'live' ? 'pointer' : 'default', backgroundColor: getStatusColor(streamer.status) }}
                >
                    {getStatusText(streamer.status)}
                    {streamer.status === 'live' && (
                        <span className="live-indicator" title="Klicken zum Ansehen">📺</span>
                    )}
                </span>
            </div>
            <div className="streamer-card-body">
                <div className="streamer-info">
                    <strong>{streamer.display_name || streamer.username}</strong>
                    <span className="username">@{streamer.username}</span>
                </div>
                <div className="channel-info">
                    <span className="channel-label">Channel:</span>
                    <span className="channel-name">{streamer.channel}</span>
                </div>
            </div>
            {/* Embedded Video Player */}
            {activeStreamEmbed === streamer.id && streamer.status === 'live' && (
                <div className="stream-embed-container">
                    <div className="stream-embed-header">
                        <span>📺 {streamer.display_name || streamer.username} - Live Stream</span>
                        <button 
                            className="embed-close-btn"
                            onClick={() => setActiveStreamEmbed(null)}
                        >
                            ✕ Schließen
                        </button>
                    </div>
                    <div className="stream-embed-wrapper">
                        <iframe
                            src={getTwitchEmbedUrl(streamer.channel)}
                            height="100%"
                            width="100%"
                            allowFullScreen
                            title={`${streamer.channel} stream`}
                        />
                    </div>
                </div>
            )}
             <div className="streamer-card-footer">
                 <div className="category-controls">
                     <button 
                         onClick={() => setEditingStreamer(streamer)}
                         className="btn-secondary"
                         style={{ padding: '6px 12px', fontSize: '0.75rem' }}
                     >
                         Bearbeiten
                     </button>
                     <button 
                         onClick={() => handleRemoveStreamer(streamer.id)}
                         className="btn-danger"
                     >
                         Entfernen
                     </button>
                 </div>
             </div>
        </div>
    );

    // Split streamers by status for organized display
    const liveStreamers = streamers.filter(s => s.status === 'live');
    const offlineStreamers = streamers.filter(s => s.status !== 'live');

    // Render a status-based section
    const renderStatusSection = (title, streamersInSection, statusColor) => {
        if (streamersInSection.length === 0) return null;
        return (
            <div className="status-section">
                <h5 className="status-section-title">
                    <span className="status-section-badge" style={{ backgroundColor: statusColor }}>
                        {title}
                    </span>
                    <span className="status-section-count">({streamersInSection.length})</span>
                </h5>
                <div className="streamers-cards">
                    {streamersInSection.map(renderStreamerCard)}
                </div>
            </div>
        );
    };

    // Toggle stream embed (video player)
    const toggleStreamEmbed = (streamerId) => {
        setActiveStreamEmbed(activeStreamEmbed === streamerId ? null : streamerId);
    };

    // Get embed URL for Twitch
    const getTwitchEmbedUrl = (channel) => {
        return `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}&muted=false`;
    };

    if (!hasPermission('manage_streamers')) {
        return (
            <div className="card" style={{ padding: '20px' }}>
                <h4>Keine Berechtigung</h4>
                <p className="muted">Sie haben keine Berechtigung für die Streamingverwaltung.</p>
            </div>
        );
    }

    return (
        <div className="streaming-management">
            <div className="section-header">
                <div className="header-title">
                    <h3>Streamingverwaltung</h3>
                    <p className="muted">Verwalte Streamer und deren Live-Status</p>
                </div>
                <button 
                    onClick={refreshStatuses} 
                    disabled={refreshing}
                    className="btn-secondary"
                >
                    {refreshing ? 'Aktualisiert...' : 'Status aktualisieren'}
                </button>
            </div>

            {/* Add Streamer Form */}
            <div className="form-section">
                <h4>Streamer hinzufügen</h4>
                <form onSubmit={handleAddStreamer} className="streamer-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label>Discord ID</label>
                            <input
                                type="text"
                                value={newStreamer.username}
                                onChange={(e) => setNewStreamer({...newStreamer, username: e.target.value})}
                                placeholder="z.B. 123456789012345678"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Anzeigename (optional)</label>
                            <input
                                type="text"
                                value={newStreamer.displayName}
                                onChange={(e) => setNewStreamer({...newStreamer, displayName: e.target.value})}
                                placeholder="z.B. Max der Streamer"
                            />
                        </div>
                        <div className="form-group">
                            <label>Kategorie</label>
                            <select
                                value={newStreamer.category}
                                onChange={(e) => setNewStreamer({...newStreamer, category: e.target.value})}
                                required
                            >
                                <option value="creator">Creator</option>
                                <option value="creator_plus">Creator+</option>
                            </select>
                        </div>
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label>Plattform *</label>
                            <select
                                value={newStreamer.platform}
                                onChange={(e) => setNewStreamer({...newStreamer, platform: e.target.value})}
                                required
                            >
                                <option value="twitch">Twitch</option>
                                <option value="tiktok">TikTok</option>
                                <option value="youtube">YouTube</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>{newStreamer.platform === 'tiktok' ? 'TikTok Username *' : 'Kanalname *'}</label>
                            <input
                                type="text"
                                value={newStreamer.channel}
                                onChange={(e) => {
                                    setNewStreamer({
                                        ...newStreamer, 
                                        channel: e.target.value,
                                        ...(newStreamer.platform === 'tiktok' ? { tiktokUsername: e.target.value } : {})
                                    });
                                }}
                                placeholder="z.B. maxmustermann"
                                required
                            />
                        </div>
                    </div>
                    
                    <button type="submit" disabled={isAdding} className="btn-primary">
                        {isAdding ? 'Wird hinzugefügt...' : 'Streamer hinzufügen'}
                    </button>
                </form>
            </div>

            {/* Streamers List */}
            <div className="list-section">
                <div className="list-header">
                    <h4>Streamer Liste</h4>
                    <span className="muted">
                        {refreshing ? 'Wird aktualisiert...' : `Automatische Aktualisierung: alle 30 Sekunden`}
                    </span>
                </div>
                {loading ? (
                    <div className="loading">Lädt...</div>
                ) : streamers.length === 0 ? (
                    <div className="no-streamers">Keine Streamer vorhanden</div>
                ) : (
                    <>
                        {renderStatusSection('Live', liveStreamers, '#22c55e')}
                        {renderStatusSection('Offline', offlineStreamers, '#ef4444')}
                    </>
                )}
            </div>

            {/* Edit Streamer Modal */}
            {editingStreamer && (
                <div className="confirmation-overlay">
                    <div className="confirmation-modal" style={{ maxWidth: '500px' }}>
                        <h4>Streamer bearbeiten</h4>
                        <div className="form-group">
                            <label>Streamer Name</label>
                            <input
                                type="text"
                                value={editingStreamer.display_name || editingStreamer.username}
                                disabled
                                style={{ opacity: 0.5 }}
                            />
                        </div>
                        <div className="form-group">
                            <label>Kategorie *</label>
                            <select
                                defaultValue={editingStreamer.category || 'creator_plus'}
                                id="edit-category-select"
                                required
                            >
                                <option value="creator">Creator</option>
                                <option value="creator_plus">Creator+</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Anzeigename</label>
                            <input
                                type="text"
                                defaultValue={editingStreamer.display_name || ''}
                                id="edit-display-name"
                                placeholder="z.B. Max der Streamer"
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
                            <button 
                                onClick={() => setEditingStreamer(null)}
                                className="btn-secondary"
                                style={{ padding: '8px 16px', fontSize: '0.8rem' }}
                            >
                                Abbrechen
                            </button>
                            <button 
                                 onClick={() => {
                                    const category = document.getElementById('edit-category-select').value;
                                    const displayName = document.getElementById('edit-display-name').value;
                                    handleEditStreamer({
                                        category: category,
                                        displayName: displayName || null
                                    });
                                }}
                                disabled={isUpdating}
                                className="btn-primary"
                                style={{ padding: '8px 16px', fontSize: '0.8rem' }}
                            >
                                {isUpdating ? 'Wird aktualisiert...' : 'Speichern'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default StreamingManagement;